// v3.15-915: Reconcile All — combined admin page with Funded Without Payment
// + Funded But Active tabs. One morning-coffee number to drive to zero.
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import FundedMismatchesPage from './FundedMismatchesPage';
import FundedButActivePage from './FundedButActivePage';
import { Loader2, RefreshCw, ScanLine } from 'lucide-react';

const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export default function ReconcileAllPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'mismatches';
  const navigate = useNavigate();

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/reconcile-summary');
      setSummary(res.data);
    } catch (e) {
      // non-blocking
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSummary(); }, []);

  return (
    <div className="space-y-4 p-6" data-testid="reconcile-all-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary flex items-center gap-2">
            <ScanLine className="h-6 w-6 text-amber-500" />
            Reconcile All
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Funding-classification exceptions in one place. Drive both numbers to zero.
          </p>
        </div>
        <Button variant="outline" onClick={fetchSummary} disabled={loading} data-testid="reconcile-refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh totals
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border-l-4 border-l-amber-500" onClick={() => fetchSummary()}>
          <CardContent className="p-3">
            <p className="text-[11px] uppercase text-slate-500 tracking-wider">Total exceptions</p>
            <p className="text-2xl font-semibold text-amber-700 mt-1" data-testid="reconcile-total-count">
              {summary?.total_count ?? '—'}
            </p>
            <p className="text-[11px] text-slate-400">{fmtMoney(summary?.total_amount)}</p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 border-l-red-500 cursor-pointer hover:shadow-md transition-shadow`} onClick={() => setParams({ tab: 'mismatches' })}>
          <CardContent className="p-3">
            <p className="text-[11px] uppercase text-slate-500 tracking-wider">Funded Without Payment</p>
            <p className="text-2xl font-semibold text-red-700 mt-1">{summary?.funded_without_payment?.count ?? '—'}</p>
            <p className="text-[11px] text-slate-400">{fmtMoney(summary?.funded_without_payment?.amount)}</p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 border-l-amber-400 cursor-pointer hover:shadow-md transition-shadow`} onClick={() => setParams({ tab: 'active' })}>
          <CardContent className="p-3">
            <p className="text-[11px] uppercase text-slate-500 tracking-wider">Funded but Active</p>
            <p className="text-2xl font-semibold text-amber-700 mt-1">{summary?.funded_but_active?.count ?? '—'}</p>
            <p className="text-[11px] text-slate-400">{fmtMoney(summary?.funded_but_active?.amount)}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList>
          <TabsTrigger value="mismatches" data-testid="tab-mismatches">Funded Without Payment</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">Funded but Active</TabsTrigger>
        </TabsList>
        <TabsContent value="mismatches" className="mt-2">
          {/* Re-uses the standalone page so all logic stays in one place. */}
          <FundedMismatchesPage />
        </TabsContent>
        <TabsContent value="active" className="mt-2">
          <FundedButActivePage />
        </TabsContent>
      </Tabs>
    </div>
  );
}
